/**
 * FounderCockpit — persistent bottom nav (rewritten 2026-07-26,
 * reconciled with C3a 2026-08-15).
 *
 * Founder direction (verbatim):
 *   "Increase the bottom navigation icons by approximately 70% to make
 *    them much easier to use. Every major area should have its own
 *    dedicated page. Bookings is the operational hub."
 *
 * Tabs (four, all lead to LIVE dedicated pages — C3a-clean):
 *   1  Dashboard  → /admin | /home  (persona-aware jump-off)
 *   2  Bookings   → /bookings   (the P0 operational hub)
 *   3  Uploads    → /upload
 *   4  Digest     → /founder/morning-digest
 *
 * The retired "More" tab was removed on 2026-08-15 when C3a
 * collapsed /more into /home. The Constitutional Toolbox (☰ button
 * top-left of every non-Work-Mode surface) is the canonical
 * discovery/manual-access surface — no cockpit slot needed.
 *
 * Icons and hit-area increased ~70% from the original 18/44 px design.
 * Talk-to-NDG is not a cockpit tab — it lives in the docked
 * ConversationBar mounted by the UniversalApplicationShell.
 */
import React, { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Calendar, Users, Car, Home, TrendingUp, MessageCircle, ClipboardCheck } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { resolveEffectiveRole } from "../../utils/roleResolver";
 import "./FounderCockpit.css";

// Paths where the cockpit MUST NOT render (auth flows, marketing, embed views).
const COCKPIT_HIDDEN_PREFIXES = [
  "/login", "/register", "/signup", "/forgot-password", "/reset-password",
  "/verify", "/legal", "/privacy", "/terms",
];
const COCKPIT_HIDDEN_EXACT = ["/"];

// C3a-compliant dashboard destination.
//
//   • Plain admin (no preview persona, not in founder mode) → /admin
//     — the admin control centre, which is NOT collapsed into /home.
//   • Admin in preview persona OR founder mode              → /home
//     — because /learn, /instructor, /pdi-hub are all C3a-retired
//     (they Navigate to="/home" replace in App.js).
//   • Learner / ADI / PDI users                             → /home
//     — same reason; single canonical home per C3a.
//
// Result: no cockpit tap can bounce off a retired route.
export function dashboardFor(user) {
  if (!user) return "/admin";
  const inPreviewOrFounderMode =
    !!user.admin_view_role || !!user.operating_mode ||
    !!user.founder_mode_active_role;
  if (inPreviewOrFounderMode) return "/home";
  const isAdmin = user.role === "admin" || user.is_admin === true;
  return isAdmin ? "/admin" : "/home";
}

export default function FounderCockpit() {
  const { user } = useAuth();
  const location = useLocation();

  const path = location.pathname;
  const shouldShow = (
    !!user &&
    !COCKPIT_HIDDEN_EXACT.includes(path) &&
    !COCKPIT_HIDDEN_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))
  );

  const isActive = useCallback((match) => {
    if (match === "/dashboard") return path === dashboardFor(user);
    return path === match || path.startsWith(`${match}/`);
  }, [path, user]);

  if (!shouldShow) return null;

  const effectiveRole = resolveEffectiveRole(user);
  const learnerView = effectiveRole === "learner" || effectiveRole === "pupil";
  // Keep the instructor list as the inspectable canonical `tabs`
  // contract; doctrine tests prove every destination remains live.
  const tabs = [
    { key: "calendar", label: "Calendar", icon: Calendar, to: "/instructor/today", testid: "cockpit-calendar-btn" },
    { key: "pupils", label: "Pupils", icon: Users, to: "/pupils", testid: "cockpit-pupils-btn" },
    { key: "work-mode", label: "Work Mode", icon: Car, to: "/work-mode", testid: "cockpit-work-mode-btn" },
  ];
  const learnerTabs = [
    { key: "learner-home", label: "Home", icon: Home, to: "/home", testid: "cockpit-learner-home-btn" },
    { key: "learner-lessons", label: "Calendar", icon: Calendar, to: "/learn/lessons", testid: "cockpit-learner-lessons-btn" },
    { key: "learner-homework", label: "Homework", icon: ClipboardCheck, to: "/learn/homework", testid: "cockpit-learner-homework-btn" },
    { key: "learner-progress", label: "Progress", icon: TrendingUp, to: "/learn/progress", testid: "cockpit-learner-progress-btn" },
    { key: "learner-messages", label: "Messages", icon: MessageCircle, to: "/learn/messages", testid: "cockpit-learner-messages-btn" },
  ];
  const visibleTabs = learnerView ? learnerTabs : tabs;

  return (
    <nav
      className="founder-cockpit"
      role="navigation"
      aria-label="Main navigation"
      data-testid="founder-cockpit"
    >
      {visibleTabs.map(({ key, label, icon: Icon, to, testid }) => {
        const active = isActive(to);
        return (
          <button key={key} type="button"
            className={`founder-cockpit__btn ${active ? "is-active" : ""}`}
            onClick={() => window.location.assign(to)} data-testid={testid} aria-label={label}
            aria-current={active ? "page" : undefined}>
            <Icon className="founder-cockpit__icon" aria-hidden="true" />
            <span className="founder-cockpit__label">{label}</span>
          </button>
        );
      })}
     </nav>
  );
}

